import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title,
  description,
  keywords,
  canonicalUrl,
  imageUrl = "https://varkbuilders.com/newlogo.png",
  schemaType = "ConstructionBusiness",
  breadcrumbs = [],
  ratingValue = "4.9",
  reviewCount = "485"
}) => {
  const defaultTitle = "Best Construction in Madurai | Top Builders in Madurai | VARK Builders";
  const defaultDescription = "VARK Builders Private Limited is the No. 1 construction company in Madurai. Specializing in luxury residential villas, budget-friendly house construction, home repairs, commercial landmarks, and turnkey civil engineering across Madurai, Usilampatti, Karaikudi, Sivaganga, Thiruppuvanam, and Theni.";
  const defaultKeywords = "Best Construction in Madurai, Top Builders in Madurai, Residential Construction in Madurai, Home Repair in Madurai, Budget Friendly House Construction in Madurai, Best Civil Engineers in Madurai, Luxury Villa Builders Madurai, Commercial Construction Madurai, Turnkey House Contractors Tamil Nadu, Best Builders in Usilampatti, Top Builders in Karaikudi, Construction Company in Sivaganga, Builders in Thiruppuvanam, Top Builders in Theni, Modern House Architects Madurai, Renovation Services Madurai";

  const siteUrl = "https://varkbuilders.com";
  const currentCanonical = canonicalUrl || siteUrl;

  // Master Construction & Local Business Schema with Google 5-Star AggregateRating
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": schemaType,
    "@id": `${siteUrl}/#organization`,
    "name": "VARK Builders Private Limited",
    "alternateName": ["VARK Builders", "VARK Construction Madurai", "VARK Builders PVT LTD"],
    "description": description || defaultDescription,
    "url": siteUrl,
    "logo": `${siteUrl}/newlogo.png`,
    "image": [
      `${siteUrl}/newlogo.png`,
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80"
    ],
    "telephone": "+919842943377",
    "email": "varkbuilders@gmail.com",
    "priceRange": "₹₹ - ₹₹₹₹",
    "currenciesAccepted": "INR",
    "paymentAccepted": "Cash, Credit Card, Bank Transfer, Cheque",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "20, 1st Floor, Annai Teresa street, Bypass Rd, Fathima Nagar",
      "addressLocality": "Madurai",
      "addressRegion": "Tamil Nadu",
      "postalCode": "625016",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 9.92154,
      "longitude": 78.094595
    },
    "hasMap": "https://maps.google.com/?q=VARK+Builders+Private+Limited+Madurai",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "09:00",
        "closes": "21:00"
      }
    ],
    "areaServed": [
      { "@type": "City", "name": "Madurai" },
      { "@type": "City", "name": "Usilampatti" },
      { "@type": "City", "name": "Karaikudi" },
      { "@type": "City", "name": "Sivaganga" },
      { "@type": "City", "name": "Thiruppuvanam" },
      { "@type": "City", "name": "Theni" },
      { "@type": "City", "name": "Coimbatore" },
      { "@type": "City", "name": "Thanjavur" },
      { "@type": "City", "name": "Kumbakonam" },
      { "@type": "City", "name": "Trichy" },
      { "@type": "AdministrativeArea", "name": "Tamil Nadu" }
    ],
    // Google SERP 5-Star Rating Rich Snippet
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": ratingValue,
      "bestRating": "5",
      "worstRating": "1",
      "ratingCount": reviewCount,
      "reviewCount": reviewCount
    },
    "review": [
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Dr. Senthil Kumar"
        },
        "datePublished": "2026-02-18",
        "reviewBody": "VARK Builders delivered our dream luxury villa on time with exceptional structural quality. Unquestionably the best construction company in Madurai!",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        }
      },
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Kavitha Rajan"
        },
        "datePublished": "2026-03-24",
        "reviewBody": "Superb turnkey house construction experience with VARK Builders. Transparent pricing, top quality materials, and completed right on schedule.",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        }
      }
    ],
    "sameAs": [
      "https://facebook.com/varkbuilders",
      "https://www.instagram.com/vark_builders_private_limited/",
      "https://www.youtube.com/@VarkBuilders",
      "https://www.linkedin.com/in/vm-samy-242011400/"
    ]
  };

  // BreadcrumbList Schema for Google Rich Snippets
  const breadcrumbSchema = breadcrumbs && breadcrumbs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url.startsWith("http") ? item.url : `${siteUrl}${item.url}`
    }))
  } : null;

  return (
    <Helmet>
      {/* Primary HTML Meta Tags */}
      <title>{title ? `${title} | VARK Builders` : defaultTitle}</title>
      <meta name="title" content={title ? `${title} | VARK Builders` : defaultTitle} />
      <meta name="description" content={description || defaultDescription} />
      <meta name="keywords" content={keywords || defaultKeywords} />
      <meta name="author" content="VARK Builders Private Limited" />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <link rel="canonical" href={currentCanonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={currentCanonical} />
      <meta property="og:site_name" content="VARK Builders Private Limited" />
      <meta property="og:title" content={title ? `${title} | VARK Builders` : defaultTitle} />
      <meta property="og:description" content={description || defaultDescription} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="VARK Builders - Best Construction Company in Madurai" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentCanonical} />
      <meta name="twitter:title" content={title ? `${title} | VARK Builders` : defaultTitle} />
      <meta name="twitter:description" content={description || defaultDescription} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content="VARK Builders Construction Landmarks" />

      {/* Geographic Tags for Google Local Search */}
      <meta name="geo.region" content="IN-TN" />
      <meta name="geo.placename" content="Madurai, Tamil Nadu" />
      <meta name="geo.position" content="9.92154;78.094595" />
      <meta name="ICBM" content="9.92154, 78.094595" />

      {/* JSON-LD Schema Structured Data (LocalBusiness + 5-Star Rating) */}
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>

      {/* JSON-LD Breadcrumb Schema */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
